import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ApiService } from '../services/api.service';
import { UtilitiesService } from '../services/utilities.service';

@Component({
    selector: 'app-reports',
    templateUrl: './reports.component.html',
    styleUrls: ['./reports.component.css'],
    standalone: false
})
export class ReportsComponent implements OnInit {

  page = 1;
  pageSize = 10;

  list_length = 0;

  token: any;
  report: any;
  month = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  year: any;
  total_appointment_payment = 0;
  package_appointment_payment = 0;

  staffs: any[] = [];

  reportMode: 'month' | 'day' = 'month';
  report_date = this.toMonthInputValue(new Date());
  report_day = this.toDayInputValue(new Date());

  constructor(public utility: UtilitiesService, private modalService: NgbModal, public api: ApiService, private router: Router) {
    this.utility.show = true;
    this.token = localStorage.getItem('access_token');
  }

  ngOnInit(): void {
    let date = new Date();
    this.year = date.getFullYear();

    this.utility.loading = true;
    this.getReport();
  }

  get services(): any[] {
    return this.report?.services_income?.by_service ?? [];
  }

  get totalServicesIncome(): number {
    return this.report?.services_income?.total ?? 0;
  }

  get totalServicesAppointments(): number {
    if (this.report?.services_income?.appointments_count != null) {
      return this.report.services_income.appointments_count;
    }
    return this.services.reduce((sum: number, service: any) => sum + (service.appointments_count ?? 0), 0);
  }

  get reportHeading(): string {
    if (this.reportMode === 'day') {
      if (this.report?.period === 'day' && this.report.day) {
        const monthName = this.month[(+this.report.month || 1) - 1] || '';
        return `Day: ${this.report.day} ${monthName} ${this.report.year}`;
      }
      if (this.report_day) {
        const [year, month, day] = this.report_day.split('-').map(Number);
        return `Day: ${day} ${this.month[month - 1]} ${year}`;
      }
      return '';
    }

    if (this.report?.month && this.report?.period !== 'day') {
      const monthName = this.month[(+this.report.month || 1) - 1] || '';
      return `Month: ${monthName} ${this.report.year}`;
    }
    if (this.report_date) {
      const [year, month] = this.report_date.split('-').map(Number);
      return `Month: ${this.month[month - 1]} ${year}`;
    }
    return '';
  }

  setReportMode(mode: 'month' | 'day'): void {
    this.reportMode = mode;
  }

  getReport() {
    this.fetchReport('appointments/report');
  }

  getReportSpecific() {
    this.utility.loading = true;
    if (this.reportMode === 'day') {
      if (!this.report_day) {
        this.utility.loading = false;
        return;
      }
      this.fetchReport(`appointments/report/date/${this.report_day}`);
      return;
    }

    const [year, month] = String(this.report_date || '').split('-');
    if (!year || !month) {
      this.utility.loading = false;
      return;
    }
    this.fetchReport(`appointments/report/${Number(month)}/${Number(year)}`);
  }

  getByStaff(staff: any, type: any, service_name?: any) {
    if (type == 'G') {
      return this.getDoctorStat(staff, 'appointments', 'count');
    }
    if (type == 'A') {
      return this.getDoctorStat(staff, 'new_patients', 'count');
    }
    if (type == 'B') {
      return this.getDoctorStat(staff, 'medical_report_requests', 'count');
    }
    if (type == 'C') {
      const service = this.services.find((item: any) => item.service_name === service_name);
      return this.findDoctorRow(service?.by_doctor ?? [], staff)?.income ?? 0;
    }
    if (type == 'N') {
      const service = this.services.find((item: any) => item.service_name === service_name);
      return this.findDoctorRow(service?.by_doctor ?? [], staff)?.appointments_count ?? 0;
    }
    if (type == 'F') {
      return this.getDoctorStat(staff, 'medical_report_requests', 'fees');
    }
    if (type == 'I') {
      return this.getDoctorStat(staff, 'services_income', 'income');
    }
    if (type == 'J') {
      return this.getDoctorStat(staff, 'services_income', 'appointments_count');
    }
    return 0;
  }

  getBonus(amount: any, appointments: number, salary: number) {
    let BF = 0;
    appointments = appointments / 30;

    if (appointments > 15 && appointments < 20)
      BF = 1.1;
    if (appointments == 20)
      BF = 1.2;
    if (appointments > 20 && appointments < 25)
      BF = 1.3;
    if (appointments == 25)
      BF = 1.4;
    if (appointments > 25)
      BF = 1.5;

    return salary * BF;
  }

  private fetchReport(path: string) {
    const sub = this.api.get(path, this.token).subscribe(
      async data => {
        this.applyReport(data);
      },
      async error => { }
    );

    sub.add(() => { this.utility.loading = false; });
  }

  private applyReport(data: any) {
    this.report = data && typeof data === 'object' ? data : null;
    this.staffs = this.staffsFromReport(this.report);
  }

  private staffsFromReport(report: any): any[] {
    if (!report) {
      return [];
    }

    const lists: any[][] = [
      report.appointments?.by_doctor,
      report.new_patients?.by_doctor,
      report.medical_report_requests?.by_doctor,
      report.services_income?.by_doctor,
      ...(report.services_income?.by_service ?? []).map((service: any) => service?.by_doctor)
    ];

    const staffs: any[] = [];
    const seen = new Set<string>();

    lists.forEach(list => {
      (list ?? []).forEach((row: any) => {
        const id = row?.doctor_id != null ? String(row.doctor_id) : '';
        const name = String(row?.doctor_name ?? '').trim();
        const key = id || this.normalizeName(name);
        if (!key || !name || seen.has(key)) {
          return;
        }
        seen.add(key);
        staffs.push({ id: id || key, name });
      });
    });

    return staffs;
  }

  private getDoctorStat(staff: any, section: string, field: string): number {
    const row = this.findDoctorRow(this.report?.[section]?.by_doctor ?? [], staff);
    return row?.[field] ?? 0;
  }

  private findDoctorRow(list: any[], staff: any): any | null {
    if (!staff || !Array.isArray(list) || list.length === 0) {
      return null;
    }

    const staffId = staff.id != null ? String(staff.id) : '';
    if (staffId) {
      const byId = list.find(item => String(item.doctor_id) === staffId);
      if (byId) {
        return byId;
      }
    }

    const staffName = this.normalizeName(staff.name);
    return list.find(item => this.normalizeName(item.doctor_name) === staffName) ?? null;
  }

  private normalizeName(name: any): string {
    return String(name ?? '').trim().toLowerCase();
  }

  private toMonthInputValue(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }

  private toDayInputValue(date: Date): string {
    return `${this.toMonthInputValue(date)}-${String(date.getDate()).padStart(2, '0')}`;
  }
}
